/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CR425: Deterministic Display Value Resolvers (SSOT)
 *
 * Single canonical resolvers used by ValueBoxBlock and SpendBoxBlock
 * to ensure deal-type-aware rendering with clear diagnostics.
 */

import type { Deal, DealCategory } from "@/lib/types/deals";
import { getDealType } from "@/lib/deals/getDealType";

// ================================================================
// TYPES
// ================================================================

export type DisplayMode = "currency" | "percent" | "empty";

export interface ValueBoxDisplay {
  mode: DisplayMode;
  /** Primary numeric value (dollar amount or percentage) */
  value: number | null;
  /** Secondary value (e.g. computed dollar value when mode=percent) */
  secondaryValue: number | null;
  /** Label for the box */
  label: string;
  /** Diagnostic reason when empty */
  reason: string | null;
  /** Deal category used for resolution */
  dealCategory: DealCategory;
}

export interface SpendBoxDisplay {
  mode: "currency" | "empty";
  value: number | null;
  label: string;
  reason: string | null;
  dealCategory: DealCategory;
}

// ================================================================
// HELPERS
// ================================================================

interface CanonicalValue {
  qualifying_spend_cad?: number | null;
  bonus_value_cad?: number | null;
  computed_value_cad?: number | null;
  effective_percent_back?: number | null;
  discount_percent?: number | null;
  discount_amount_cad?: number | null;
  /** CR313: Synonym used by normalize pipeline */
  discount_value_cad?: number | null;
  pay_amount_cad?: number | null;
  receive_value_cad?: number | null;
}

interface CanonicalEligibility {
  buy?: { amount_cad?: number | null };
  get?: { amount_cad?: number | null };
}

function getCanonicalValue(deal: any): CanonicalValue {
  return (deal.canonical_deal_json?.value as CanonicalValue) ?? {};
}

function getCanonicalEligibility(deal: any): CanonicalEligibility {
  return (deal.canonical_deal_json?.eligibility_requirements as CanonicalEligibility) ?? {};
}

function num(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? parseFloat(v) : (v as number);
  return !isNaN(n) && isFinite(n) && n > 0 ? n : null;
}

// ================================================================
// VALUE BOX RESOLVER
// ================================================================

export function getValueBoxDisplay(deal: Deal): ValueBoxDisplay {
  const { category } = getDealType(deal);
  const cv = getCanonicalValue(deal);
  const er = getCanonicalEligibility(deal);
  const checked: string[] = [];

  // --- BONUS ---
  if (category === "BONUS") {
    // Prefer computed_value_cad (currency)
    const computedVal = num(cv.computed_value_cad) ?? num(deal.computed_value_cad);
    checked.push("computed_value_cad");
    if (computedVal != null) {
      const pct = num(cv.effective_percent_back) ?? num(deal.effective_percent_back);
      return { mode: "currency", value: computedVal, secondaryValue: pct, label: "Effective Value", reason: null, dealCategory: category };
    }

    const bonusVal = num(cv.bonus_value_cad) ?? num((deal as any).bonus_value_cad) ?? num(deal.bonus_amount);
    checked.push("bonus_value_cad", "bonus_amount");
    if (bonusVal != null) {
      return { mode: "currency", value: bonusVal, secondaryValue: null, label: "Effective Value", reason: null, dealCategory: category };
    }

    const pct = num(cv.effective_percent_back) ?? num(deal.effective_percent_back);
    checked.push("effective_percent_back");
    if (pct != null) {
      return { mode: "percent", value: pct, secondaryValue: null, label: "Effective Value", reason: null, dealCategory: category };
    }

    return { mode: "empty", value: null, secondaryValue: null, label: "Effective Value", reason: `Checked: ${checked.join(", ")}`, dealCategory: category };
  }

  // --- DISCOUNT ---
  if (category === "DISCOUNT") {
    const computedVal = num(cv.computed_value_cad) ?? num(deal.computed_value_cad);
    checked.push("computed_value_cad");
    if (computedVal != null) {
      return { mode: "currency", value: computedVal, secondaryValue: null, label: "Discount Value", reason: null, dealCategory: category };
    }

    const discountAmt = num(cv.discount_amount_cad) ?? num(cv.discount_value_cad) ?? num(deal.discount_amount_cad);
    checked.push("discount_amount_cad", "discount_value_cad");
    if (discountAmt != null) {
      return { mode: "currency", value: discountAmt, secondaryValue: null, label: "Discount Value", reason: null, dealCategory: category };
    }

    // Derive from eligibility_requirements
    const erGet = num(er.get?.amount_cad);
    const erBuy = num(er.buy?.amount_cad);
    checked.push("eligibility_requirements.get.amount_cad");
    if (erGet != null) {
      const derivedPct = erBuy ? Math.round(100 * erGet / erBuy) : null;
      return { mode: "currency", value: erGet, secondaryValue: derivedPct, label: "Discount Value", reason: null, dealCategory: category };
    }

    const effPct = num(cv.effective_percent_back) ?? num(deal.effective_percent_back);
    checked.push("effective_percent_back");
    if (effPct != null) {
      return { mode: "percent", value: effPct, secondaryValue: null, label: "Discount Value", reason: null, dealCategory: category };
    }

    const discPct = num(cv.discount_percent) ?? num(deal.discount_percent);
    checked.push("discount_percent");
    if (discPct != null) {
      return { mode: "percent", value: discPct, secondaryValue: null, label: "Discount", reason: null, dealCategory: category };
    }

    return { mode: "empty", value: null, secondaryValue: null, label: "Discount Value", reason: `Checked: ${checked.join(", ")}`, dealCategory: category };
  }

  // --- POINTS ---
  if (category === "POINTS") {
    // Points use PointsValueBoxBlock; this resolver provides fallback data
    const cashVal = num(deal.computed_value_cad) ?? num(cv.computed_value_cad);
    checked.push("computed_value_cad");
    if (cashVal != null) {
      return { mode: "currency", value: cashVal, secondaryValue: null, label: "Cash Equivalent", reason: null, dealCategory: category };
    }
    return { mode: "empty", value: null, secondaryValue: null, label: "Cash Equivalent", reason: `Checked: ${checked.join(", ")}`, dealCategory: category };
  }

  // --- IN_KIND ---
  if (category === "IN_KIND") {
    const estVal = num((deal as any).estimated_value_cad) ?? num(deal.computed_value_cad) ?? num((deal as any).in_kind_value_cad);
    checked.push("estimated_value_cad", "computed_value_cad", "in_kind_value_cad");
    if (estVal != null) {
      return { mode: "currency", value: estVal, secondaryValue: null, label: "Estimated Value", reason: null, dealCategory: category };
    }
    return { mode: "empty", value: null, secondaryValue: null, label: "Estimated Value", reason: `Checked: ${checked.join(", ")}`, dealCategory: category };
  }

  // --- UNCLASSIFIED fallback ---
  const fallbackVal = num(deal.computed_value_cad);
  const fallbackPct = num(deal.effective_percent_back);
  checked.push("computed_value_cad", "effective_percent_back");
  if (fallbackPct != null) {
    return { mode: "percent", value: fallbackPct, secondaryValue: fallbackVal, label: "Effective Value", reason: null, dealCategory: category };
  }
  if (fallbackVal != null) {
    return { mode: "currency", value: fallbackVal, secondaryValue: null, label: "Effective Value", reason: null, dealCategory: category };
  }
  return { mode: "empty", value: null, secondaryValue: null, label: "Effective Value", reason: `Checked: ${checked.join(", ")}`, dealCategory: category };
}

// ================================================================
// SPEND BOX RESOLVER
// ================================================================

export function getSpendBoxDisplay(deal: Deal): SpendBoxDisplay {
  const { category } = getDealType(deal);
  const cv = getCanonicalValue(deal);
  const er = getCanonicalEligibility(deal);
  const checked: string[] = [];

  const label = category === "DISCOUNT" ? "Minimum Spend" : "Qualifying Spend";

  // Priority: top-level qualifying_spend_cad > canonical value > spend_amount > eligibility buy amount
  const qs = num(deal.qualifying_spend_cad);
  checked.push("qualifying_spend_cad");
  if (qs != null) return { mode: "currency", value: qs, label, reason: null, dealCategory: category };

  const cvQs = num(cv.qualifying_spend_cad);
  checked.push("canonical.value.qualifying_spend_cad");
  if (cvQs != null) return { mode: "currency", value: cvQs, label, reason: null, dealCategory: category };

  const sa = num(deal.spend_amount) ?? num((deal as any).spend_threshold_amount);
  checked.push("spend_amount", "spend_threshold_amount");
  if (sa != null) return { mode: "currency", value: sa, label, reason: null, dealCategory: category };

  const minSpend = num((deal as any).min_spend_cad) ?? num(deal.min_purchase_cad);
  checked.push("min_spend_cad", "min_purchase_cad");
  if (minSpend != null) return { mode: "currency", value: minSpend, label, reason: null, dealCategory: category };

  const erBuy = num(er.buy?.amount_cad);
  checked.push("eligibility_requirements.buy.amount_cad");
  if (erBuy != null) return { mode: "currency", value: erBuy, label, reason: null, dealCategory: category };

  return { mode: "empty", value: null, label, reason: `Checked: ${checked.join(", ")}`, dealCategory: category };
}

// ================================================================
// DEBUG SUMMARY (for BoxDataChip)
// ================================================================

export interface BoxDataSummary {
  value: {
    computed: number | null;
    disc_pct: number | null;
    eff_pct: number | null;
    disc_amt: number | null;
  };
  spend: {
    qualifying: number | null;
    spend_amt: number | null;
    er_buy: number | null;
  };
}

export function getBoxDataSummary(deal: Deal): BoxDataSummary {
  const cv = getCanonicalValue(deal);
  const er = getCanonicalEligibility(deal);
  return {
    value: {
      computed: num(cv.computed_value_cad) ?? num(deal.computed_value_cad),
      disc_pct: num(cv.discount_percent) ?? num(deal.discount_percent),
      eff_pct: num(cv.effective_percent_back) ?? num(deal.effective_percent_back),
      disc_amt: num(cv.discount_amount_cad) ?? num(deal.discount_amount_cad),
    },
    spend: {
      qualifying: num(deal.qualifying_spend_cad) ?? num(cv.qualifying_spend_cad),
      spend_amt: num(deal.spend_amount),
      er_buy: num(er.buy?.amount_cad),
    },
  };
}
